import { axios } from './axios'

/*
This is the DynVPN API Rest client.
*/

export class APIClient {
  constructor() {
    this.apiKey = null
    this.email = null
    this.extraHeaders = {}
  }

  hasAPIKey() {
    return !!axios.defaults.headers.common['X-netvfy-apikey']
  }

  setAPIKey(key) {
    axios.defaults.headers.common['X-netvfy-apikey'] = key
  }

  clearAPIKey() {
    // delete the API key
    delete axios.defaults.headers.common['X-netvfy-apikey']
  }

  setEmail(email) {
    this.email = email
    this.extraHeaders['X-netvfy-email'] = email
  }

  clearEmail() {
    this.email = null
    this.extraHeaders = {}
  }

  _noReturnData(opts) {
    // All calls using this method return no data
    return new Promise((resolve, reject) => {
      axios(opts)
        .then(response => {
          resolve(true)
        })
        .catch(reject)
    })
  }

  signup(creds) {
    // this API call currently does not send an email
    let url = '/client'
    const opts = { url, data: creds, method: 'post' }
    return this._noReturnData(opts)
  }

  activate(creds) {
    let url = '/client/activate'
    const opts = { url, data: creds, method: 'post' }
    return this._noReturnData(opts)
  }

  login(creds) {
    // the api doesn't have a login endpoint. however, it has a "newapikey" endpoint
    // that returns a new api key from user credentials
    this.clearAPIKey()
    let url = '/client/newapikey'
    const opts = { url, data: creds, method: 'post' }
    return new Promise((resolve, reject) => {
      axios(opts)
        .then(response => {
          let apikey = response.data.client.apikey
          this.setAPIKey(apikey)
          this.setEmail(creds.email)
          resolve({ apiKey: apikey, email: creds.email })
        })
        .catch(reject)
    })
  }

  logout() {
    return new Promise((resolve, reject) => {
      this.clearAPIKey()
      this.clearEmail()
      resolve(true)
    })
  }

  resetKey(creds) {
    // this API call currently does not send an email
    let url = '/client/newresetkey'
    const opts = { url, data: creds, method: 'post' }
    return this._noReturnData(opts)
  }

  resetPassword(creds) {
    let url = '/client/password'
    const opts = { url, data: creds, method: 'post' }
    return this._noReturnData(opts)
  }

  getNodesFor(network) {
    // fetch all nodes for network and return updated network
    let url = `/node?network_uid=${network.uid}`
    const opts = { url, method: 'get', headers: this.extraHeaders }
    return new Promise((resolve, reject) => {
      axios(opts)
        .then(response => {
          resolve({ ...network, ...response.data })
        })
        .catch(e => {
          reject(Error(`Error fetching network: ${network.uid}`))
        })
    })
  }

  getAllNetworks() {
    // get all networks for the current api key
    const url = '/network'
    const opts = { headers: this.extraHeaders, url, method: 'get' }
    return new Promise((resolve, reject) => {
      // first get the networks
      axios(opts)
        .then(response => {
          const { networks } = response.data
          // map over networks and fetch nodes
          let requests = networks.map(network => this.getNodesFor(network))
          return Promise.all(requests)
        })
        .then(resolve)
        .catch(e => {
          reject(Error('Error fetching networks'))
        })
    })
  }

  addNetwork(item) {
    const url = '/network'
    const opts = { headers: this.extraHeaders, url, data: item, method: 'post' }
    return this._noReturnData(opts)
  }

  deleteNetwork(item) {
    const url = `/network?description=${item.description}`
    const opts = { headers: this.extraHeaders, url, method: 'delete' }
    return this._noReturnData(opts)
  }

  addNode(item) {
    const url = '/node'
    const opts = { headers: this.extraHeaders, url, data: item, method: 'post' }
    return this._noReturnData(opts)
  }

  deleteNode(item, network) {
    const url = `/node?network_description=${network.description}&description=${item.description}`
    const opts = { headers: this.extraHeaders, url, method: 'delete' }
    return this._noReturnData(opts)
  }

  getAllRegions() {
    // get all regions for the current api key
    const url = '/regions'
    const opts = { url, method: 'get' }
    return new Promise((resolve, reject) => {
      axios(opts)
        .then(({ data }) => {
          resolve(data.regions)
        })
        .catch(reject)
    })
  }
}

// export pre-configured singleton apiClient instance
export let apiClient = new APIClient()
